// ---- nirvana.query.buildGroupedFocusView ----
if (!window.nirvana) window.nirvana = {};
if (!nirvana.query) nirvana.query = {};

// ------------------------------------------
// Helper: Safely convert legacy comma-delimited tags string to array
// ------------------------------------------
function getTagArray(t) {
  // If t.tags is already an array, return as is; if string, split on comma
  if (Array.isArray(t.tags)) return t.tags;
  if (typeof t.tags === 'string') {
    return t.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }
  return [];
}

// ------------------------------------------
// Helper: Flat list builder for state views
// ------------------------------------------
nirvana.query._buildFlatList = function (
  state,
  showCompleted,
  sortField = 'seqt'
) {
  const tasks = Object.values(NIRV.tasks);

  const isNext = (s) => s === NIRV.CONST.PROJECT || s === NIRV.CONST.NEXT;
  const isSomeday = (s) => s === NIRV.CONST.SOMEDAY;
  // Special handling for Scheduled view: include both SCHEDULED and RECURRING
  const isMatchState = (t) => {
    const s = parseInt(t.state);
    if (state === NIRV.CONST.PROJECT) return isNext(s);
    if (state === NIRV.CONST.SOMEDAY) return isSomeday(s);
    if (state === NIRV.CONST.SCHEDULED)
      return s === NIRV.CONST.SCHEDULED || s === NIRV.CONST.RECURRING;
    return s === state;
  };

  const flatList = tasks.filter((t) => {
    const type = parseInt(t.type);
    const completed = parseInt(t.completed);
    return (
      isMatchState(t) &&
      (showCompleted || completed === 0) &&
      [
        NIRV.CONST.TASK,
        NIRV.CONST.PROJECT,
        NIRV.CONST.REFITEM,
        NIRV.CONST.REFLIST,
      ].includes(type)
    );
  });

  flatList.sort((a, b) => parseInt(a[sortField]) - parseInt(b[sortField]));

  return flatList;
};

// ------------------------------------------
// Helper: Grouped view builder for state views
// ------------------------------------------
nirvana.query._buildGroupedView = function (state, showCompleted) {
  const tasks = Object.values(NIRV.tasks);
  const result = {
    standalone: { tasks: [], projects: [], reflists: [] },
    areafolders: [],
  };

  const byParent = {};
  for (const t of tasks) {
    const id = t.id;
    const parentid = t.parentid || '';
    if (!byParent[parentid]) byParent[parentid] = [];
    byParent[parentid].push(t);
  }

  const isAreafolder = (t) => parseInt(t.type) === NIRV.CONST.AREAFOLDER;
  const isProject = (t) => parseInt(t.type) === NIRV.CONST.PROJECT;
  const isTask = (t) => parseInt(t.type) === NIRV.CONST.TASK;
  const isReflist = (t) => parseInt(t.type) === NIRV.CONST.REFLIST;
  const isRefitem = (t) => parseInt(t.type) === NIRV.CONST.REFITEM;

  const isNext = (s) => s === NIRV.CONST.PROJECT || s === NIRV.CONST.NEXT;
  const isSomeday = (s) => s === NIRV.CONST.SOMEDAY;
  const isMatch = (t) => {
    const s = parseInt(t.state);
    const completed = parseInt(t.completed);
    const matchState =
      (state === NIRV.CONST.PROJECT && isNext(s)) ||
      (state === NIRV.CONST.SOMEDAY && isSomeday(s)) ||
      (state !== NIRV.CONST.PROJECT &&
        state !== NIRV.CONST.SOMEDAY &&
        s === state);
    return matchState && (showCompleted || completed === 0);
  };

  // Build areafolder map
  const areafolderMap = {};
  for (const t of tasks) {
    if (isAreafolder(t))
      areafolderMap[t.id] = {
        id: t.id,
        name: t.name,
        seqt: parseInt(t.seqt),
        seq: parseInt(t.seq),
        tasks: [],
        projects: [],
        reflists: [],
      };
  }

  // --- Pass 1: Add all qualifying children, and collect parents as needed ---
  // We'll keep track of parent projects/reflists that need to be included.
  const parentProjects = {};
  const parentReflists = {};

  for (const t of tasks) {
    const type = parseInt(t.type);
    const pid = t.parentid || '';
    if (!isMatch(t)) continue;

    if (pid === '' || !NIRV.tasks[pid]) {
      if (isTask(t)) result.standalone.tasks.push(t);
      else if (isProject(t))
        result.standalone.projects.push({ ...t, tasks: [] });
      else if (isReflist(t))
        result.standalone.reflists.push({ ...t, items: [] });
    } else {
      const parent = NIRV.tasks[pid];
      const ptype = parseInt(parent?.type);
      if (ptype === NIRV.CONST.PROJECT && isTask(t)) {
        // Find or create project in standalone or areafolder
        let proj =
          result.standalone.projects.find((p) => p.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.projects)
            .find((p) => p.id === pid);
        if (!proj) {
          proj = { ...parent, tasks: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.projects.push(proj);
          else result.standalone.projects.push(proj);
        }
        proj.tasks.push(t);
        parentProjects[pid] = proj;
      } else if (ptype === NIRV.CONST.REFLIST && isRefitem(t)) {
        let rl =
          result.standalone.reflists.find((r) => r.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.reflists)
            .find((r) => r.id === pid);
        if (!rl) {
          rl = { ...parent, items: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.reflists.push(rl);
          else result.standalone.reflists.push(rl);
        }
        rl.items.push(t);
        parentReflists[pid] = rl;
      } else if (ptype === NIRV.CONST.AREAFOLDER) {
        const af = areafolderMap[pid];
        if (!af) continue;
        if (isTask(t)) af.tasks.push(t);
        else if (isProject(t)) af.projects.push({ ...t, tasks: [] });
        else if (isReflist(t)) af.reflists.push({ ...t, items: [] });
      }
    }
  }

  // --- Pass 2: Ensure all parent projects and reflists with qualifying children are included even if they don't match isMatch ---
  // For projects
  Object.values(parentProjects).forEach((proj) => {
    // If project already present in standalone or areafolder, skip
    let exists =
      result.standalone.projects.find((p) => p.id === proj.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.projects)
        .find((p) => p.id === proj.id);
    if (!exists) {
      // Place in areafolder if parentid is an areafolder, else standalone
      const af = areafolderMap[proj.parentid];
      if (af) af.projects.push(proj);
      else result.standalone.projects.push(proj);
    }
  });
  // For reflists
  Object.values(parentReflists).forEach((rl) => {
    let exists =
      result.standalone.reflists.find((r) => r.id === rl.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.reflists)
        .find((r) => r.id === rl.id);
    if (!exists) {
      const af = areafolderMap[rl.parentid];
      if (af) af.reflists.push(rl);
      else result.standalone.reflists.push(rl);
    }
  });

  // --- Pass 3: Attach project tasks and reflist items under areafolders, sort children ---
  // For Next, Later, Someday, Waiting: sort top-level tasks by seq, tasks inside projects and items inside reflists by seqp.
  // For other views: fallback to previous behavior (seqt for top-level tasks, seqp for children).
  const useSeqForTop =
    state === NIRV.CONST.NEXT ||
    state === NIRV.CONST.LATER ||
    state === NIRV.CONST.SOMEDAY ||
    state === NIRV.CONST.WAITING;
  const sortTasks = (a, b) => parseInt(a.seq) - parseInt(b.seq);
  const sortChildren = (a, b) => parseInt(a.seqp) - parseInt(b.seqp);

  for (const af of Object.values(areafolderMap)) {
    af.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.tasks.sort(
      useSeqForTop ? sortTasks : (a, b) => parseInt(a.seqt) - parseInt(b.seqt)
    );
    for (const proj of af.projects) {
      proj.tasks = (byParent[proj.id] || [])
        .filter((t) => isTask(t) && isMatch(t))
        .sort(sortChildren);
    }
    for (const rl of af.reflists) {
      rl.items = (byParent[rl.id] || [])
        .filter((t) => isRefitem(t) && isMatch(t))
        .sort(sortChildren);
    }
  }

  // Sort standalone projects and reflists by seq
  result.standalone.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  // Sort standalone tasks and their children (tasks/items)
  result.standalone.tasks.sort(
    useSeqForTop ? sortTasks : (a, b) => parseInt(a.seqt) - parseInt(b.seqt)
  );
  for (const p of result.standalone.projects) p.tasks.sort(sortChildren);
  for (const r of result.standalone.reflists) r.items.sort(sortChildren);

  result.areafolders = Object.values(areafolderMap)
    .filter((af) => af.tasks.length || af.projects.length || af.reflists.length)
    .sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  return result;
};

// ==========================
// 1. Inbox
// ==========================

// Flat list of inbox tasks
nirvana.query.buildFlatInboxList = function () {
  const tasks = Object.values(NIRV.tasks).filter(
    (t) =>
      parseInt(t.state) === NIRV.CONST.INBOX &&
      [
        NIRV.CONST.TASK,
        NIRV.CONST.PROJECT,
        NIRV.CONST.REFITEM,
        NIRV.CONST.REFLIST,
      ].includes(parseInt(t.type))
  );
  tasks.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  return tasks;
};

// ==========================
// 2. Focus
// ==========================

// Grouped Focus view (standalone, projects, areafolders)
nirvana.query.buildGroupedFocusView = function () {
  const tasks = Object.values(NIRV.tasks);
  const result = {
    standalone: { tasks: [], projects: [], reflists: [] },
    areafolders: [],
  };

  const showCompleted = NIRV.prefs.UIFocusShowCompleted?.value == '1';

  const isAreafolder = (t) => parseInt(t.type) === NIRV.CONST.AREAFOLDER;
  const isProject = (t) => parseInt(t.type) === NIRV.CONST.PROJECT;
  const isTask = (t) => parseInt(t.type) === NIRV.CONST.TASK;
  const isReflist = (t) => parseInt(t.type) === NIRV.CONST.REFLIST;
  const isRefitem = (t) => parseInt(t.type) === NIRV.CONST.REFITEM;
  const isInFocus = (t) => parseInt(t.seqt) !== 0;
  const isIncomplete = (t) => showCompleted || parseInt(t.completed) === 0;
  const isMatch = (t) => isInFocus(t) && isIncomplete(t);

  // Index by parent for quick lookup
  const byParent = {};
  for (const t of tasks) {
    const parentid = t.parentid || '';
    if (!byParent[parentid]) byParent[parentid] = [];
    byParent[parentid].push(t);
  }

  // Pass 1: Index areafolders
  const areafolderMap = {};
  for (const t of tasks) {
    if (isAreafolder(t))
      areafolderMap[t.id] = {
        id: t.id,
        name: t.name,
        seqt: parseInt(t.seqt),
        seq: parseInt(t.seq),
        tasks: [],
        projects: [],
        reflists: [],
      };
  }

  // Pass 2: Process all items, adding parents/projects/reflists as needed if they have children that match
  for (const t of tasks) {
    const type = parseInt(t.type);
    const pid = t.parentid || '';
    if (!isMatch(t)) continue;

    if (pid === '' || !NIRV.tasks[pid]) {
      // Top-level
      if (isTask(t)) result.standalone.tasks.push(t);
      else if (isProject(t))
        result.standalone.projects.push({ ...t, tasks: [] });
      else if (isReflist(t))
        result.standalone.reflists.push({ ...t, items: [] });
    } else {
      const parent = NIRV.tasks[pid];
      const ptype = parseInt(parent?.type);
      if (ptype === NIRV.CONST.PROJECT && isTask(t)) {
        // Find or create project in standalone/projects or areafolders
        let proj =
          result.standalone.projects.find((p) => p.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.projects)
            .find((p) => p.id === pid);
        if (!proj) {
          proj = { ...parent, tasks: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.projects.push(proj);
          else result.standalone.projects.push(proj);
        }
        proj.tasks.push(t);
      } else if (ptype === NIRV.CONST.REFLIST && isRefitem(t)) {
        // Find or create reflist in standalone/reflists or areafolders
        let rl =
          result.standalone.reflists.find((r) => r.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.reflists)
            .find((r) => r.id === pid);
        if (!rl) {
          rl = { ...parent, items: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.reflists.push(rl);
          else result.standalone.reflists.push(rl);
        }
        rl.items.push(t);
      } else if (ptype === NIRV.CONST.AREAFOLDER) {
        // Under areafolder
        const af = areafolderMap[pid];
        if (!af) continue;
        if (isTask(t)) af.tasks.push(t);
        else if (isProject(t)) af.projects.push({ ...t, tasks: [] });
        else if (isReflist(t)) af.reflists.push({ ...t, items: [] });
      }
    }
  }

  // Attach project tasks and reflist items under areafolders (and sort)
  for (const af of Object.values(areafolderMap)) {
    af.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.tasks.sort((a, b) => parseInt(a.seqt) - parseInt(b.seqt));
    for (const proj of af.projects) {
      proj.tasks = (byParent[proj.id] || [])
        .filter((t) => isTask(t) && isMatch(t))
        .sort((a, b) => parseInt(a.seqp) - parseInt(b.seqp));
    }
    for (const rl of af.reflists) {
      rl.items = (byParent[rl.id] || [])
        .filter((t) => isRefitem(t) && isMatch(t))
        .sort((a, b) => parseInt(a.seqp) - parseInt(b.seqp));
    }
  }

  // Sort standalone projects and reflists by seq
  result.standalone.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  // Sort standalone tasks and their children by correct field
  result.standalone.tasks.sort((a, b) => parseInt(a.seqt) - parseInt(b.seqt));
  for (const p of result.standalone.projects)
    p.tasks.sort((a, b) => parseInt(a.seqp) - parseInt(b.seqp));
  for (const r of result.standalone.reflists)
    r.items.sort((a, b) => parseInt(a.seqp) - parseInt(b.seqp));

  result.areafolders = Object.values(areafolderMap)
    .filter((af) => af.tasks.length || af.projects.length || af.reflists.length)
    .sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  return result;
};

// Flat list of Focus tasks
nirvana.query.buildFlatFocusList = function () {
  const tasks = Object.values(NIRV.tasks);
  const showCompleted = NIRV.prefs.UIFocusShowCompleted?.value == '1';

  const isInFocus = (t) => parseInt(t.seqt) !== 0;
  const isIncomplete = (t) => showCompleted || parseInt(t.completed) === 0;

  const flatList = tasks.filter((t) => {
    const type = parseInt(t.type);
    return (
      isInFocus(t) &&
      isIncomplete(t) &&
      [
        NIRV.CONST.TASK,
        NIRV.CONST.PROJECT,
        NIRV.CONST.REFITEM,
        NIRV.CONST.REFLIST,
      ].includes(type)
    );
  });

  flatList.sort((a, b) => parseInt(a.seqt) - parseInt(b.seqt));

  return flatList;
};

// ==========================
// 3. Next
// ==========================

// Grouped Next view
nirvana.query.buildGroupedNextView = function () {
  return nirvana.query._buildGroupedView(
    NIRV.CONST.NEXT,
    NIRV.prefs.UINextShowCompleted?.value == '1'
  );
};

// Flat list of Next tasks
nirvana.query.buildFlatNextList = function () {
  return nirvana.query._buildFlatList(
    NIRV.CONST.NEXT,
    NIRV.prefs.UINextShowCompleted?.value == '1',
    'seq'
  );
};

// ==========================
// 4. Later
// ==========================

// Grouped Later view
nirvana.query.buildGroupedLaterView = function () {
  return nirvana.query._buildGroupedView(
    NIRV.CONST.LATER,
    NIRV.prefs.UILaterShowCompleted?.value == '1'
  );
};

// Flat list of Later tasks
nirvana.query.buildFlatLaterList = function () {
  return nirvana.query._buildFlatList(
    NIRV.CONST.LATER,
    NIRV.prefs.UILaterShowCompleted?.value == '1',
    'seq'
  );
};

// ==========================
// 5. Scheduled
// ==========================

// Flat list of Scheduled tasks, sorted by startdate and then seq for tie-breaking
nirvana.query.buildFlatScheduledList = function () {
  const tasks = nirvana.query._buildFlatList(NIRV.CONST.SCHEDULED, true);
  tasks.sort((a, b) => {
    const ad = parseInt(a.startdate);
    const bd = parseInt(b.startdate);
    if (ad !== bd) return ad - bd;
    return parseInt(a.seq) - parseInt(b.seq);
  });
  return tasks;
};

// ==========================
// 6. Someday
// ==========================

// Grouped Someday view
nirvana.query.buildGroupedSomedayView = function () {
  return nirvana.query._buildGroupedView(
    NIRV.CONST.SOMEDAY,
    NIRV.prefs.UISomedayShowCompleted?.value == '1'
  );
};

// Flat list of Someday tasks
nirvana.query.buildFlatSomedayList = function () {
  return nirvana.query._buildFlatList(
    NIRV.CONST.SOMEDAY,
    NIRV.prefs.UISomedayShowCompleted?.value == '1'
  );
};

// ==========================
// 7. Waiting
// ==========================

// Grouped Waiting view
nirvana.query.buildGroupedWaitingView = function () {
  return nirvana.query._buildGroupedView(
    NIRV.CONST.WAITING,
    NIRV.prefs.UIWaitingShowCompleted?.value == '1'
  );
};

// Flat list of Waiting tasks
nirvana.query.buildFlatWaitingList = function () {
  return nirvana.query._buildFlatList(
    NIRV.CONST.WAITING,
    NIRV.prefs.UIWaitingShowCompleted?.value == '1'
  );
};

// ==========================
// 8. Logged
// ==========================

// Flat list of Logged tasks
nirvana.query.buildFlatLoggedList = function () {
  const tasks = Object.values(NIRV.tasks).filter(
    (t) =>
      parseInt(t.state) === NIRV.CONST.LOGGED &&
      [
        NIRV.CONST.TASK,
        NIRV.CONST.PROJECT,
        NIRV.CONST.REFITEM,
        NIRV.CONST.REFLIST,
      ].includes(parseInt(t.type))
  );
  // Sort by completed date descending
  tasks.sort((a, b) => parseInt(b.completed) - parseInt(a.completed));
  return tasks;
};

// ==========================
// 9. Trash
// ==========================

// Flat list of Trash tasks
nirvana.query.buildFlatTrashList = function () {
  const tasks = Object.values(NIRV.tasks).filter(
    (t) =>
      parseInt(t.state) === NIRV.CONST.TRASHED &&
      [
        NIRV.CONST.TASK,
        NIRV.CONST.PROJECT,
        NIRV.CONST.REFITEM,
        NIRV.CONST.REFLIST,
      ].includes(parseInt(t.type))
  );
  tasks.sort((a, b) => a.name.localeCompare(b.name));
  return tasks;
};

// ==========================
// 10. Search & Tag Views
// ==========================

// Grouped search view builder
// Returns tasks matching a search term (in name, note, or tags)
nirvana.query.buildGroupedSearchView = function (searchTerm) {
  const term = String(searchTerm).trim().toLowerCase();
  const tasks = Object.values(NIRV.tasks);

  const matches = tasks.filter((t) => {
    if (parseInt(t.state) === NIRV.CONST.LOGGED) return false;
    const name = (t.name || '').toLowerCase();
    const note = (t.note || '').toLowerCase();
    const tags = getTagArray(t).join(' ').toLowerCase();
    return name.includes(term) || note.includes(term) || tags.includes(term);
  });

  const result = {
    standalone: { tasks: [], projects: [], reflists: [] },
    areafolders: [],
  };

  const isAreafolder = (t) => parseInt(t.type) === NIRV.CONST.AREAFOLDER;
  const isProject = (t) => parseInt(t.type) === NIRV.CONST.PROJECT;
  const isTask = (t) => parseInt(t.type) === NIRV.CONST.TASK;
  const isReflist = (t) => parseInt(t.type) === NIRV.CONST.REFLIST;
  const isRefitem = (t) => parseInt(t.type) === NIRV.CONST.REFITEM;

  // Index by parent for quick lookup
  const byParent = {};
  for (const t of tasks) {
    const pid = t.parentid || '';
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(t);
  }

  // Pass 1: Index areafolders
  const areafolderMap = {};
  for (const t of tasks) {
    if (isAreafolder(t))
      areafolderMap[t.id] = {
        id: t.id,
        name: t.name,
        seqt: parseInt(t.seqt),
        seq: parseInt(t.seq),
        tasks: [],
        projects: [],
        reflists: [],
      };
  }

  // We'll keep track of parent projects/reflists that need to be included.
  const parentProjects = {};
  const parentReflists = {};

  for (const t of matches) {
    const pid = t.parentid || '';
    const type = parseInt(t.type);

    if (pid === '' || !NIRV.tasks[pid]) {
      if (isTask(t)) result.standalone.tasks.push(t);
      else if (isProject(t))
        result.standalone.projects.push({ ...t, tasks: [] });
      else if (isReflist(t))
        result.standalone.reflists.push({ ...t, items: [] });
    } else {
      const parent = NIRV.tasks[pid];
      const ptype = parseInt(parent?.type);

      if (ptype === NIRV.CONST.PROJECT && isTask(t)) {
        let proj =
          result.standalone.projects.find((p) => p.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.projects)
            .find((p) => p.id === pid);
        if (!proj) {
          proj = { ...parent, tasks: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.projects.push(proj);
          else result.standalone.projects.push(proj);
        }
        proj.tasks.push(t);
        parentProjects[pid] = proj;
      } else if (ptype === NIRV.CONST.REFLIST && isRefitem(t)) {
        let rl =
          result.standalone.reflists.find((r) => r.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.reflists)
            .find((r) => r.id === pid);
        if (!rl) {
          rl = { ...parent, items: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.reflists.push(rl);
          else result.standalone.reflists.push(rl);
        }
        rl.items.push(t);
        parentReflists[pid] = rl;
      } else if (ptype === NIRV.CONST.AREAFOLDER) {
        const af = areafolderMap[pid];
        if (!af) continue;
        if (isTask(t)) af.tasks.push(t);
        else if (isProject(t)) af.projects.push({ ...t, tasks: [] });
        else if (isReflist(t)) af.reflists.push({ ...t, items: [] });
      }
    }
  }

  // Ensure all parent projects and reflists with qualifying children are included even if they don't match isMatch
  Object.values(parentProjects).forEach((proj) => {
    const exists =
      result.standalone.projects.find((p) => p.id === proj.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.projects)
        .find((p) => p.id === proj.id);
    if (!exists) {
      const af = areafolderMap[proj.parentid];
      if (af) af.projects.push(proj);
      else result.standalone.projects.push(proj);
    }
  });

  Object.values(parentReflists).forEach((rl) => {
    const exists =
      result.standalone.reflists.find((r) => r.id === rl.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.reflists)
        .find((r) => r.id === rl.id);
    if (!exists) {
      const af = areafolderMap[rl.parentid];
      if (af) af.reflists.push(rl);
      else result.standalone.reflists.push(rl);
    }
  });

  const sortChildren = (a, b) => parseInt(a.seqp) - parseInt(b.seqp);
  const sortTasks = (a, b) => parseInt(a.seq) - parseInt(b.seq);

  for (const af of Object.values(areafolderMap)) {
    af.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.tasks.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    for (const proj of af.projects) proj.tasks.sort(sortChildren);
    for (const rl of af.reflists) rl.items.sort(sortChildren);
  }

  result.standalone.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.tasks.sort(sortTasks);
  for (const p of result.standalone.projects) p.tasks.sort(sortChildren);
  for (const r of result.standalone.reflists) r.items.sort(sortChildren);

  result.areafolders = Object.values(areafolderMap)
    .filter((af) => af.tasks.length || af.projects.length || af.reflists.length)
    .sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  return result;
};

// Grouped tag view builder
// Returns all tasks that include the specified tag
nirvana.query.buildGroupedTagView = function (tag) {
  const term = String(tag).trim().toLowerCase();
  const tasks = Object.values(NIRV.tasks);

  const matches = tasks.filter((t) => {
    if (parseInt(t.state) === NIRV.CONST.LOGGED) return false;
    const tags = getTagArray(t).map((tg) => tg.toLowerCase());
    return tags.includes(term);
  });

  return nirvana.query._buildGroupedSearchLikeView(matches);
};

// ==========================
// 11. Projects
// ==========================

// Flat list of top-level Projects, grouped by state and sorted by seq within each group
nirvana.query.buildFlatProjectList = function () {
  const groups = {
    [NIRV.CONST.ACTIVEPROJECT]: [],
    [NIRV.CONST.LATER]: [],
    [NIRV.CONST.SCHEDULED]: [],
    [NIRV.CONST.SOMEDAY]: [],
  };

  for (const t of Object.values(NIRV.tasks)) {
    if (
      parseInt(t.type) === NIRV.CONST.PROJECT &&
      (!t.parentid || !NIRV.tasks[t.parentid])
    ) {
      const state = parseInt(t.state);
      if (groups[state]) {
        groups[state].push(t);
      }
    }
  }

  Object.values(groups).forEach((arr) =>
    arr.sort((a, b) => parseInt(a.seq) - parseInt(b.seq))
  );

  return [
    ...groups[NIRV.CONST.ACTIVEPROJECT],
    ...groups[NIRV.CONST.LATER],
    ...groups[NIRV.CONST.SCHEDULED],
    ...groups[NIRV.CONST.SOMEDAY],
  ];
};

// ==========================
// 12. Reflists
// ==========================

// Flat list of all top-level Reflists
nirvana.query.buildFlatReflistList = function () {
  const tasks = Object.values(NIRV.tasks).filter(
    (t) =>
      parseInt(t.type) === NIRV.CONST.REFLIST &&
      (!t.parentid || !NIRV.tasks[t.parentid]) // only top-level
  );
  tasks.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  return tasks;
};

// ==========================
// 13. Reflist Items by Reflist ID
// ==========================

// Flat list of Refitems under a given Reflist
nirvana.query.viewReflist = function (reflistId) {
  return Object.values(NIRV.tasks)
    .filter(
      (t) => parseInt(t.type) === NIRV.CONST.REFITEM && t.parentid === reflistId
    )
    .sort((a, b) => parseInt(a.seqp) - parseInt(b.seqp));
};

// ==========================
// 14. Project Tasks by Project ID
// ==========================

// Flat list of Tasks under a given Project, grouped by state and sorted by seqp
nirvana.query.viewProject = function (projectId) {
  const taskGroups = {
    [NIRV.CONST.NEXT]: [],
    [NIRV.CONST.LATER]: [],
    [NIRV.CONST.WAITING]: [],
    [NIRV.CONST.SCHEDULED]: [],
    [NIRV.CONST.SOMEDAY]: [],
    [NIRV.CONST.LOGGED]: [],
  };

  for (const t of Object.values(NIRV.tasks)) {
    if (
      parseInt(t.type) === NIRV.CONST.TASK &&
      t.parentid === projectId &&
      taskGroups.hasOwnProperty(parseInt(t.state))
    ) {
      taskGroups[parseInt(t.state)].push(t);
    }
  }

  // Sort each group appropriately
  taskGroups[NIRV.CONST.NEXT].sort(
    (a, b) => parseInt(a.seqp) - parseInt(b.seqp)
  );
  taskGroups[NIRV.CONST.LATER].sort(
    (a, b) => parseInt(a.seqp) - parseInt(b.seqp)
  );
  taskGroups[NIRV.CONST.WAITING].sort(
    (a, b) => parseInt(a.seqp) - parseInt(b.seqp)
  );
  taskGroups[NIRV.CONST.SOMEDAY].sort(
    (a, b) => parseInt(a.seqp) - parseInt(b.seqp)
  );
  // SCHEDULED: sort by startdate, then seq
  taskGroups[NIRV.CONST.SCHEDULED].sort((a, b) => {
    const sa = parseInt(a.startdate);
    const sb = parseInt(b.startdate);
    if (sa !== sb) return sa - sb;
    return parseInt(a.seqp) - parseInt(b.seqp);
  });
  // LOGGED: sort by created
  taskGroups[NIRV.CONST.LOGGED].sort(
    (a, b) => parseInt(a.created) - parseInt(b.created)
  );

  return [
    ...taskGroups[NIRV.CONST.NEXT],
    ...taskGroups[NIRV.CONST.LATER],
    ...taskGroups[NIRV.CONST.WAITING],
    ...taskGroups[NIRV.CONST.SCHEDULED],
    ...taskGroups[NIRV.CONST.SOMEDAY],
    ...taskGroups[NIRV.CONST.LOGGED],
  ];
};

// Internal shared helper for search/tag view builds
// Accepts a list of matched tasks and groups them into the view structure
nirvana.query._buildGroupedSearchLikeView = function (matches) {
  const tasks = Object.values(NIRV.tasks);
  const result = {
    standalone: { tasks: [], projects: [], reflists: [] },
    areafolders: [],
  };

  const isAreafolder = (t) => parseInt(t.type) === NIRV.CONST.AREAFOLDER;
  const isProject = (t) => parseInt(t.type) === NIRV.CONST.PROJECT;
  const isTask = (t) => parseInt(t.type) === NIRV.CONST.TASK;
  const isReflist = (t) => parseInt(t.type) === NIRV.CONST.REFLIST;
  const isRefitem = (t) => parseInt(t.type) === NIRV.CONST.REFITEM;

  // Index by parent for quick lookup
  const byParent = {};
  for (const t of tasks) {
    const pid = t.parentid || '';
    if (!byParent[pid]) byParent[pid] = [];
    byParent[pid].push(t);
  }

  // Pass 1: Index areafolders
  const areafolderMap = {};
  for (const t of tasks) {
    if (isAreafolder(t))
      areafolderMap[t.id] = {
        id: t.id,
        name: t.name,
        seqt: parseInt(t.seqt),
        seq: parseInt(t.seq),
        tasks: [],
        projects: [],
        reflists: [],
      };
  }

  // We'll keep track of parent projects/reflists that need to be included.
  const parentProjects = {};
  const parentReflists = {};

  for (const t of matches) {
    const pid = t.parentid || '';
    const type = parseInt(t.type);

    if (pid === '' || !NIRV.tasks[pid]) {
      if (isTask(t)) result.standalone.tasks.push(t);
      else if (isProject(t))
        result.standalone.projects.push({ ...t, tasks: [] });
      else if (isReflist(t))
        result.standalone.reflists.push({ ...t, items: [] });
    } else {
      const parent = NIRV.tasks[pid];
      const ptype = parseInt(parent?.type);

      if (ptype === NIRV.CONST.PROJECT && isTask(t)) {
        let proj =
          result.standalone.projects.find((p) => p.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.projects)
            .find((p) => p.id === pid);
        if (!proj) {
          proj = { ...parent, tasks: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.projects.push(proj);
          else result.standalone.projects.push(proj);
        }
        proj.tasks.push(t);
        parentProjects[pid] = proj;
      } else if (ptype === NIRV.CONST.REFLIST && isRefitem(t)) {
        let rl =
          result.standalone.reflists.find((r) => r.id === pid) ||
          Object.values(areafolderMap)
            .flatMap((af) => af.reflists)
            .find((r) => r.id === pid);
        if (!rl) {
          rl = { ...parent, items: [] };
          const af = areafolderMap[parent.parentid];
          if (af) af.reflists.push(rl);
          else result.standalone.reflists.push(rl);
        }
        rl.items.push(t);
        parentReflists[pid] = rl;
      } else if (ptype === NIRV.CONST.AREAFOLDER) {
        const af = areafolderMap[pid];
        if (!af) continue;
        if (isTask(t)) af.tasks.push(t);
        else if (isProject(t)) af.projects.push({ ...t, tasks: [] });
        else if (isReflist(t)) af.reflists.push({ ...t, items: [] });
      }
    }
  }

  // Ensure all parent projects and reflists with qualifying children are included even if they don't match isMatch
  Object.values(parentProjects).forEach((proj) => {
    const exists =
      result.standalone.projects.find((p) => p.id === proj.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.projects)
        .find((p) => p.id === proj.id);
    if (!exists) {
      const af = areafolderMap[proj.parentid];
      if (af) af.projects.push(proj);
      else result.standalone.projects.push(proj);
    }
  });

  Object.values(parentReflists).forEach((rl) => {
    const exists =
      result.standalone.reflists.find((r) => r.id === rl.id) ||
      Object.values(areafolderMap)
        .flatMap((af) => af.reflists)
        .find((r) => r.id === rl.id);
    if (!exists) {
      const af = areafolderMap[rl.parentid];
      if (af) af.reflists.push(rl);
      else result.standalone.reflists.push(rl);
    }
  });

  const sortChildren = (a, b) => parseInt(a.seqp) - parseInt(b.seqp);
  const sortTasks = (a, b) => parseInt(a.seq) - parseInt(b.seq);

  for (const af of Object.values(areafolderMap)) {
    af.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    af.tasks.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
    for (const proj of af.projects) proj.tasks.sort(sortChildren);
    for (const rl of af.reflists) rl.items.sort(sortChildren);
  }

  result.standalone.projects.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.reflists.sort((a, b) => parseInt(a.seq) - parseInt(b.seq));
  result.standalone.tasks.sort(sortTasks);
  for (const p of result.standalone.projects) p.tasks.sort(sortChildren);
  for (const r of result.standalone.reflists) r.items.sort(sortChildren);

  result.areafolders = Object.values(areafolderMap)
    .filter((af) => af.tasks.length || af.projects.length || af.reflists.length)
    .sort((a, b) => parseInt(a.seq) - parseInt(b.seq));

  return result;
};

// ------------------------------------------
// Helper: Render grouped view to Markdown
// ------------------------------------------
nirvana.query.renderGroupedViewToMarkdown = function (viewName, data) {
  const lines = [`# ${viewName}`];
  const INDENT = '   ';
  // Accepts either a unix timestamp or a YYYYMMDD string, returns YYYY-MM-DD
  const formatDate = (raw) => {
    const str = String(raw);
    // If string is 8 digits, assume YYYYMMDD
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6)}`;
    }
    // Otherwise, treat as unix timestamp
    const d = new Date(parseInt(str) * 1000);
    return d.toISOString().slice(0, 10);
  };
  // formatTask: include completed date prefix, startdate prefix and duedate suffix
  const formatTask = (t, indent = '') => {
    const isFocus = parseInt(t.seqt) !== 0;
    const isSomeday = parseInt(t.state) === NIRV.CONST.SOMEDAY;
    const checkbox = `[${parseInt(t.completed) ? 'x' : ' '}]`;
    const focusMark = isFocus ? '• ' : '';
    const somedayMark = isSomeday ? '~ ' : '';
    const completedPrefix = parseInt(t.completed)
      ? `(${formatDate(t.completed)}) `
      : '';
    const startPrefix = t.startdate ? `${formatDate(t.startdate)} ` : '';
    const dueSuffix = t.duedate ? ` (due ${formatDate(t.duedate)})` : '';
    return `${indent}- ${checkbox} ${focusMark}${somedayMark}${completedPrefix}${startPrefix}${t.name}${dueSuffix}`;
  };
  const formatProject = (p) => `### 📁 ${p.name}`;
  const formatReflist = (r) => `[${r.name}]`;

  // Render standalone section
  const standalone = data.standalone;
  if (standalone) {
    // Render standalone tasks, in order
    for (const task of standalone.tasks) {
      lines.push(formatTask(task));
    }

    // Render projects and their tasks, in order
    for (const proj of standalone.projects) {
      lines.push(formatProject(proj));
      for (const task of proj.tasks || []) {
        lines.push(formatTask(task, INDENT));
      }
    }

    // Render reflists and their items, in order
    for (const rl of standalone.reflists) {
      lines.push(formatReflist(rl));
      for (const item of rl.items || []) {
        lines.push(formatTask(item, INDENT));
      }
    }
  }

  // Render areafolders, in order
  for (const af of data.areafolders || []) {
    lines.push('');
    lines.push(`## 🗂️ ${af.name}`);

    // Render standalone tasks, in order
    for (const task of af.tasks) {
      lines.push(formatTask(task));
    }

    // Render projects and their tasks, in order
    for (const proj of af.projects) {
      lines.push(formatProject(proj));
      for (const task of proj.tasks || []) {
        lines.push(formatTask(task, INDENT));
      }
    }

    // Render reflists and their items, in order
    for (const rl of af.reflists) {
      lines.push(formatReflist(rl));
      for (const item of rl.items || []) {
        lines.push(formatTask(item, INDENT));
      }
    }
  }

  return lines.join('\n');
};

// ------------------------------------------
// Helper: Render flat list to Markdown
// ------------------------------------------
nirvana.query.renderFlatListToMarkdown = function (viewName, flatList) {
  const lines = [`# ${viewName}`];
  // Accepts either a unix timestamp or a YYYYMMDD string, returns YYYY-MM-DD
  const formatDate = (raw) => {
    const str = String(raw);
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6)}`;
    }
    const d = new Date(parseInt(str) * 1000);
    return d.toISOString().slice(0, 10);
  };
  for (const t of flatList) {
    const isFocus = parseInt(t.seqt) !== 0;
    const isSomeday = parseInt(t.state) === NIRV.CONST.SOMEDAY;
    const checkbox = `[${parseInt(t.completed) ? 'x' : ' '}]`;
    const focusMark = isFocus ? '• ' : '';
    const somedayMark = isSomeday ? '~ ' : '';
    const completedPrefix = parseInt(t.completed)
      ? `(${formatDate(t.completed)}) `
      : '';
    const startPrefix = t.startdate ? `${formatDate(t.startdate)} ` : '';
    const parent = t.parentid ? NIRV.tasks?.[t.parentid] : null;
    const parentName = parent?.name ? ` (${parent.name})` : '';
    const dueSuffix = t.duedate ? ` (due ${formatDate(t.duedate)})` : '';
    lines.push(
      `- ${checkbox} ${focusMark}${somedayMark}${completedPrefix}${startPrefix}${t.name}${parentName}${dueSuffix}`
    );
  }
  return lines.join('\n');
};

// ------------------------------------------
// Helper: Render flat list of Projects grouped by state
// ------------------------------------------
nirvana.query.renderFlatProjectListToMarkdown = function () {
  const list = nirvana.query.buildFlatProjectList();
  const lines = ['# Projects'];

  const formatDate = (raw) => {
    const str = String(raw);
    if (/^\d{8}$/.test(str)) {
      return `${str.slice(0, 4)}-${str.slice(4, 6)}-${str.slice(6)}`;
    }
    const d = new Date(parseInt(str) * 1000);
    return d.toISOString().slice(0, 10);
  };

  const labelMap = {
    [NIRV.CONST.ACTIVEPROJECT]: '## Active',
    [NIRV.CONST.LATER]: '## Later',
    [NIRV.CONST.SCHEDULED]: '## Scheduled',
    [NIRV.CONST.SOMEDAY]: '## Someday',
  };

  let currentState = null;
  for (const t of list) {
    const s = parseInt(t.state);
    if (s !== currentState) {
      lines.push('');
      lines.push(labelMap[s] || `## State ${s}`);
      currentState = s;
    }
    const completedPrefix = parseInt(t.completed)
      ? `(${formatDate(t.completed)}) `
      : '';
    const startPrefix = t.startdate ? `${formatDate(t.startdate)} ` : '';
    const dueSuffix = t.duedate ? ` (due ${formatDate(t.duedate)})` : '';
    lines.push(
      `- [${
        parseInt(t.completed) ? 'x' : ' '
      }] ${completedPrefix}${startPrefix}${t.name}${dueSuffix}`
    );
  }

  return lines.join('\n');
};
